import { useEffect, useState } from 'react';
import toastLib, { Toast, Toaster, ToastPosition } from 'react-hot-toast';

import './styles.scss';

export type ToastKind = 'success' | 'error' | 'warning' | 'info';

/** Ícones de traço (24×24, currentColor) — sem lib de ícones a mais no bundle. */
const ICONS: Record<ToastKind, JSX.Element> = {
	success: (
		<path d="M20 6 9 17l-5-5" />
	),
	error: (
		<>
			<path d="M12 8v5" />
			<path d="M12 16.5h.01" />
			<circle cx="12" cy="12" r="9" />
		</>
	),
	warning: (
		<>
			<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
			<path d="M12 9v4" />
			<path d="M12 17h.01" />
		</>
	),
	info: (
		<>
			<circle cx="12" cy="12" r="9" />
			<path d="M12 16v-4" />
			<path d="M12 8h.01" />
		</>
	)
};

/** Erro fica mais tempo: o comprador precisa ler o motivo antes de tentar de novo. */
const DURATION: Record<ToastKind, number> = {
	success: 4000,
	info: 5000,
	warning: 7000,
	error: 8000
};

function ToastCard({
	t,
	kind,
	message
}: {
	t: Toast;
	kind: ToastKind;
	message: string;
}) {
	return (
		<div
			className={`ck-toast ck-toast--${kind} ${
				t.visible ? 'ck-toast--in' : 'ck-toast--out'
			}`}
			role={kind === 'error' ? 'alert' : 'status'}
			aria-live={kind === 'error' ? 'assertive' : 'polite'}
		>
			<span className="ck-toast__icon" aria-hidden>
				<svg
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth={2.2}
					strokeLinecap="round"
					strokeLinejoin="round"
				>
					{ICONS[kind]}
				</svg>
			</span>
			<p className="ck-toast__message">{message}</p>
			<button
				type="button"
				className="ck-toast__close"
				aria-label="Fechar"
				onClick={() => toastLib.dismiss(t.id)}
			>
				<svg
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth={2}
					strokeLinecap="round"
				>
					<path d="M18 6 6 18M6 6l12 12" />
				</svg>
			</button>
		</div>
	);
}

/**
 * Mostra um toast. O `id` é o próprio texto: a mesma mensagem repetida (duplo
 * clique, retry) substitui a anterior em vez de empilhar — o `preventDuplicates`
 * que o toastr fazia.
 */
export function showToast(kind: ToastKind, message: unknown) {
	const text = String(message ?? '').trim();
	if (!text) return;
	toastLib.custom((t) => <ToastCard t={t} kind={kind} message={text} />, {
		id: `${kind}:${text}`,
		duration: DURATION[kind]
	});
}

const MOBILE = '(max-width: 640px)';

/**
 * Montado uma vez, na raiz do app. Nunca embaixo no centro: é onde fica o
 * "Finalizar compra", e o toast cobria o botão. Desktop no canto inferior
 * direito (fora do formulário); celular no topo, longe do botão fixo de baixo.
 */
export function ToastViewport() {
	const [position, setPosition] = useState<ToastPosition>(() =>
		typeof window !== 'undefined' && window.matchMedia(MOBILE).matches
			? 'top-center'
			: 'bottom-right'
	);

	useEffect(() => {
		const query = window.matchMedia(MOBILE);
		const update = () =>
			setPosition(query.matches ? 'top-center' : 'bottom-right');
		query.addEventListener('change', update);
		return () => query.removeEventListener('change', update);
	}, []);

	return (
		<Toaster
			position={position}
			gutter={10}
			containerClassName="ck-toast-viewport"
		/>
	);
}
